/**
 * Utility functions for offer description functionality
 */

import { useMemo } from 'react'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

const MAX_OFFER_DESCRIPTION_POSITION = 3

/**
 * Determines if a description should be shown for an offer based on position and loading state
 * @param position - The position of the offer in the list (0-based)
 * @param descriptionLoading - Whether descriptions are being loaded (from socket event)
 * @returns true if description should be shown
 */
export function shouldShowOfferDescription(
  position: number | undefined,
  descriptionLoading?: boolean,
): boolean {
  return !!(
    descriptionLoading &&
    position !== undefined &&
    position < MAX_OFFER_DESCRIPTION_POSITION
  )
}

/**
 * Hook to determine if offer descriptions should be shown.
 * Combines position-based logic with the NLS descriptions A/B test.
 *
 * The experiment `search_nls_descriptions` runs within the `search-natural-language-search-ab-test`
 * experiment group and is only activated when:
 * - User is in the NLS experiment
 * - The current search is an NLS search (descriptionLoading is true)
 * - Control group: Shows descriptions (current behavior)
 * - Variant group: Hides descriptions (testing removal of AI summaries)
 *
 * @param position - The position of the offer in the list (0-based)
 * @param descriptionLoading - Whether descriptions are being loaded (from socket event, indicates NLS search)
 * @returns true if description should be shown
 */
export function useShouldShowOfferDescription(
  position: number | undefined,
  descriptionLoading?: boolean,
): boolean {
  const isInNlsExperiment = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)

  const shouldActivateNlsDescriptionsExperiment = isInNlsExperiment && !!descriptionLoading

  const nlsDescriptionsVariant = useOptimizelyExperiment(
    OptimizelyExperiments.searchNlsDescriptions,
    shouldActivateNlsDescriptionsExperiment,
  )
  // Variant group hides descriptions, control group shows them (current behavior)
  const shouldHideDescriptions = !!nlsDescriptionsVariant && nlsDescriptionsVariant !== 'control'

  return useMemo(() => {
    // If user is in the variant group, hide descriptions
    if (shouldHideDescriptions) {
      return false
    }

    // Default behavior: show descriptions based on position and loading state
    return shouldShowOfferDescription(position, descriptionLoading)
  }, [descriptionLoading, position, shouldHideDescriptions])
}
